import { useCallback, useState } from 'react';
import { ApolloError } from '@apollo/client';
import { Link, useNavigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';

import {
  Button,
  LinkItem,
  Typography,
  EditIcon,
  ContentCopyOutlinedIcon,
  VisibilityOutlinedIcon,
} from '@occc/ui-design-system';
import {
  useAppLayoutContextConsumer,
  NotFoundPage,
  OfferSupportAlert,
  Page,
} from '@occc-app/design-system';
import { useCopyOffer } from '@occc-app/offerHelpers/hooks/useCopyOffer';
import { useIsUserPermittedTo, Feature } from '@occc/utils-access-control';
import {
  Offer,
  ComponentDetails,
  ConstraintSupplementalData,
} from '@occc-app/routes/CampaignComponents/component-details';
import { CopyConfirmDialog } from '@occc-app/routes/CampaignComponents/components/copy-confirm-dialog.component';
import { getAwardSubtype, getComponentDetailsVisibilityForAward } from './utils';

import { routeCreator } from '../utils';

type AwardDetailsPageProps = {
  offer?: Offer;
  constraintSupplementalData?: ConstraintSupplementalData;
  loading: boolean;
  error?: ApolloError;
  links: LinkItem[];
};

export const AwardDetailsPage = ({
  offer,
  constraintSupplementalData,
  loading,
  error,
  links,
}: AwardDetailsPageProps) => {
  const navigate = useNavigate();

  const isUserPermittedToCreate = useIsUserPermittedTo(Feature.AWARD_CREATE);
  const isUserPermittedToEdit = useIsUserPermittedTo(Feature.AWARD_EDIT);
  const isUserPermittedToView = useIsUserPermittedTo(Feature.AWARD_EDIT_VIEW_ONLY);

  const [isCopyDialogOpen, setIsCopyDialogOpen] = useState(false);
  const [isCopyInProgress, setIsCopyInProgress] = useState(false);

  const copyOffer = useCopyOffer();
  const openCopyDialog = useCallback(() => setIsCopyDialogOpen(true), []);
  const closeCopyDialog = useCallback(() => setIsCopyDialogOpen(false), []);

  const onCopyConfirm = useCallback(async () => {
    if (!offer) {
      return;
    }

    setIsCopyInProgress(true);
    const offerId = await copyOffer({ offer });

    setIsCopyInProgress(false);
    closeCopyDialog();

    if (offerId) {
      navigate(routeCreator.update(offerId));
    }
  }, [offer, copyOffer, closeCopyDialog, navigate]);

  useAppLayoutContextConsumer(links);

  if (error) {
    return <Typography color='error'>Something went wrong. Please try again.</Typography>;
  }

  if (loading && !offer) {
    return <CircularProgress />;
  }

  if (!offer) {
    return <NotFoundPage />;
  }

  const isSupported = !!getAwardSubtype(offer);
  const isEditButtonVisible = isSupported && isUserPermittedToEdit;
  const isCopyButtonVisible = isSupported && isUserPermittedToCreate;
  const isViewButtonVisible = isSupported && !isUserPermittedToEdit && isUserPermittedToView;

  return (
    <Page
      browserTabTitle='Award Details'
      pageHeaderProps={{
        title: offer.name,
        subtitle: offer.description || undefined,
      }}
      actions={
        <>
          {isEditButtonVisible && (
            <Button
              color='secondary'
              variant='contained'
              component={Link}
              to={routeCreator.update(offer.id)}
              startIcon={<EditIcon />}
              role='button'
            >
              Edit
            </Button>
          )}
          {isCopyButtonVisible && (
            <Button
              color='secondary'
              variant='contained'
              startIcon={<ContentCopyOutlinedIcon />}
              onClick={openCopyDialog}
            >
              Copy
            </Button>
          )}
          {isViewButtonVisible && (
            <Button
              color='secondary'
              variant='contained'
              component={Link}
              to={routeCreator.update(offer.id)}
              startIcon={<VisibilityOutlinedIcon />}
              role='button'
            >
              View Configuration
            </Button>
          )}
        </>
      }
    >
      {!isSupported && (isUserPermittedToEdit || isUserPermittedToView) && (
        <Box mb={2}>
          <OfferSupportAlert
            title='Program management does not yet support this configuration.'
            fallbackLink={`/offers/${offer.id}`}
            fallbackLinkTitle='View the original offer'
          />
        </Box>
      )}

      <ComponentDetails
        offer={offer}
        visibleSections={getComponentDetailsVisibilityForAward(offer)}
        constraintSupplementalData={constraintSupplementalData}
      />

      <CopyConfirmDialog
        componentName='Award'
        open={isCopyDialogOpen}
        loading={isCopyInProgress}
        onClose={closeCopyDialog}
        onConfirm={onCopyConfirm}
      />
    </Page>
  );
};
